import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, TrendingUp, CreditCard, Upload, LogOut, Menu, X, FileText, Settings, Filter, Package, Target, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { API, clearSession, getAuthHeaders } from '@/utils/api';

const DIVISIONS = ["Arvind Tresca DTR", "Arvind Tresca WS", "GG DTR", "GG WS", "TAS"];

// Initialize division from localStorage
const getInitialDivision = () => {
  try {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('selectedDivision') || 'all';
    }
  } catch { /* storage blocked (private mode) — fall back */ }
  return 'all';
};

export default function Layout({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState(getInitialDivision);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearSession(); // Clear localStorage session
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearSession(); // Clear localStorage session even on error
      navigate('/login');
    }
  };
  
  // Security kill-switch: revoke EVERY session for this account (all devices),
  // for use if a session is suspected stolen. Logs this browser out too.
  const handleLogoutAll = async () => {
    if (!window.confirm('Sign out of ALL devices?\n\nThis revokes every active session for your account — every other browser/phone is signed out, and so is this one. Use it if you think someone else has access.')) return;
    try {
      // Send the Bearer token too (like ProtectedRoute) — if the cookie is
      // missing/blocked, a cookie-only call would 401 and revoke NOTHING.
      const res = await axios.post(`${API}/auth/logout-all`, {}, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      clearSession();
      toast.success(`Signed out of ${res.data?.revoked ?? 'all'} session(s)`);
      navigate('/login');
    } catch (error) {
      // Do NOT pretend it worked: other devices' sessions were NOT revoked.
      toast.error('Could not revoke other sessions — you are signed out on this device only. Try again once you are back online.');
      clearSession();
      navigate('/login');
    }
  };

  const handleDivisionChange = (value) => {
    setSelectedDivision(value);
    try { localStorage.setItem('selectedDivision', value); } catch { /* storage blocked */ }
  };
  
  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/outstanding', icon: FileText, label: 'Outstanding Invoices' },
    { path: '/customers', icon: Users, label: 'Customers' },
    { path: '/sales', icon: TrendingUp, label: 'Sales Analytics' },
    // Payment Tracking hidden for now (no live payment data uploaded yet); the
    // /payments route is retained so it can be restored when that begins.
    // { path: '/payments', icon: CreditCard, label: 'Payment Tracking' },
    { path: '/order-summary', icon: ClipboardList, label: 'Order Summary' },
    { path: '/orders-dispatch', icon: Package, label: 'Orders & Dispatch' },
    { path: '/sales-targets', icon: Target, label: 'Sales Targets' },
    { path: '/import', icon: Upload, label: 'Import Data', adminOnly: true },
    { path: '/settings', icon: Settings, label: 'Settings', adminOrAccounts: true },
  ];
  
  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 fixed md:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transition-transform duration-300 ease-in-out`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="py-6 px-5 border-b border-slate-800 text-center">
            <h1
              className="text-3xl font-extrabold tracking-tight"
              style={{
                background: 'linear-gradient(135deg, #e0f2fe, #ffffff 40%, #c4b5fd)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Deomart PRO
            </h1>
            <p
              className="text-base tracking-wide mt-1"
              style={{
                fontFamily: "'Caveat', cursive",
                background: 'linear-gradient(90deg, #67e8f9, #a78bfa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Retail &amp; Relationships
            </p>
          </div>
          
          {/* Division Filter */}
          <div className="px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <Filter size={14} className="text-slate-400" />
              <span className="text-xs text-slate-400 uppercase tracking-wide">Division Filter</span>
            </div>
            <Select value={selectedDivision} onValueChange={handleDivisionChange}>
              <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="font-medium">All Divisions</span>
                </SelectItem>
                {DIVISIONS.map((div) => (
                  <SelectItem key={div} value={div}>{div}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems
              .filter(item => {
                if (item.adminOnly) return user?.role === 'admin';
                if (item.adminOrAccounts) return user?.role === 'admin' || user?.role === 'accounts';
                return true;
              })
              .map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-indigo-600/20 text-indigo-300 border-r-2 border-indigo-500'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-body font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
          
          {/* User info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <img
                src={user?.picture || 'https://via.placeholder.com/40'}
                alt={user?.name}
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
            <Button
              data-testid="logout-all-button"
              onClick={handleLogoutAll}
              variant="ghost"
              title="Revoke every active session for your account, across all devices"
              className="w-full justify-start text-slate-400 hover:bg-slate-800 hover:text-rose-300 text-xs"
            >
              <LogOut size={13} className="mr-2" />
              Sign out all devices
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="md:hidden bg-white border-b border-border p-4 flex items-center justify-between">
          <h1
            className="text-xl font-extrabold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #c4b5fd)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Deomart PRO
          </h1>
          <Button
            data-testid="mobile-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            variant="ghost"
            size="icon"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </header>
        
        {/* Desktop header with division indicator */}
        <header className="hidden md:flex bg-white border-b border-border px-6 py-3 items-center justify-between">
          <div className="flex items-center gap-2">
            {selectedDivision !== 'all' && (
              <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                {selectedDivision}
              </span>
            )}
          </div>
          <span
            className="text-base font-extrabold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #c4b5fd)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Deomart PRO
          </span>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-auto bg-background">
          <Outlet context={{ user, selectedDivision }} />
        </main>
      </div>
    </div>
  );
}
